import React, { useState } from 'react';

const EXPLANATION_LIMITS = Object.freeze({
  summary: 480,
  factor: 320,
  factors: 6,
  limitations: 600,
});

function boundedExplanationText(value, maximum) {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  if (!text || text.length > maximum) return '';
  return text;
}

function toggleExplanationFromKeyboard(event) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const disclosure = event.currentTarget.closest('details');
  if (!disclosure) return;
  event.preventDefault();
  disclosure.open = !disclosure.open;
}

// Explanations cross a provider-to-UI trust boundary. Only the documented,
// user-facing fields survive; provider metadata, hidden reasoning, traces,
// prompts, and unexpected objects are never rendered.
export function sanitizeResponseExplanation(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const summary = boundedExplanationText(value.summary, EXPLANATION_LIMITS.summary);
  const limitations = boundedExplanationText(value.limitations, EXPLANATION_LIMITS.limitations);
  const factors = [];
  const seen = new Set();
  if (Array.isArray(value.factors)) {
    value.factors.forEach((candidate) => {
      if (factors.length >= EXPLANATION_LIMITS.factors) return;
      const factor = boundedExplanationText(candidate, EXPLANATION_LIMITS.factor);
      const key = factor.toLocaleLowerCase();
      if (!factor || seen.has(key)) return;
      seen.add(key);
      factors.push(factor);
    });
  }
  if (!summary && !limitations && factors.length === 0) return null;
  return Object.freeze({ summary, factors: Object.freeze(factors), limitations });
}

export default function MessageBubble({ msg, isPlaying = false, onPlay, onStop, onEdit }) {
  const isUser = msg.sender === 'user';
  const [copied, setCopied] = useState(false);
  const explanation = isUser ? null : sanitizeResponseExplanation(msg.explanation);
  const timestamp = msg.created_at || msg.createdAt;
  const timestampLabel = timestamp && !Number.isNaN(new Date(timestamp).getTime())
    ? new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(new Date(timestamp))
    : '';

  const copyText = async () => {
    try {
      await navigator.clipboard?.writeText(msg.text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };

  return (
    <div
      data-testid="chat-message"
      data-role={isUser ? 'user' : 'assistant'}
      data-message-id={String(msg.id || '')}
      className={`br-message flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`br-message__bubble break-words ${
          isUser
            ? 'br-message__bubble--user'
            : 'br-message__bubble--assistant'
        }`}
      >
        <div data-testid="chat-message-content">{msg.text}</div>
        {timestampLabel && <time className="br-message__time" dateTime={new Date(timestamp).toISOString()}>{timestampLabel}</time>}
        {isUser && (
          <div className="br-message__edit">
            <button type="button" onClick={() => onEdit?.(msg)} aria-label="Edit this message" title="Edit message" className="br-message__edit-button">
              <svg aria-hidden="true" viewBox="0 0 24 24"><path d="m5 15.8-.8 4 4-.8L18.7 8.5a1.8 1.8 0 0 0 0-2.6l-.6-.6a1.8 1.8 0 0 0-2.6 0L5 15.8Z" /><path d="m13.8 7 3.2 3.2" /></svg>
            </button>
          </div>
        )}
        {!isUser && (
          <div className="mt-2 flex items-center gap-1 opacity-75 transition hover:opacity-100">
            <button
              type="button"
              onClick={() => (isPlaying ? onStop?.() : onPlay?.(msg))}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-white/70 transition hover:bg-white/10 hover:text-white"
              aria-label={isPlaying ? 'Stop reading this response' : 'Read this response aloud'}
              title={isPlaying ? 'Stop audio' : 'Read aloud'}
            >
              {isPlaying ? <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current"><rect x="7" y="7" width="10" height="10" rx="1" /></svg> : <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-2"><path d="M11 5 6.5 9H3v6h3.5L11 19zM15 9.5a4 4 0 0 1 0 5M18 7a7.5 7.5 0 0 1 0 10" /></svg>}
            </button>
            <button type="button" onClick={copyText} aria-label="Copy this response" title="Copy" className="inline-flex h-7 w-7 items-center justify-center rounded-md text-white/70 transition hover:bg-white/10 hover:text-white">
              {copied ? <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-2"><path d="m5 12 4 4L19 6" /></svg> : <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-2"><rect x="8" y="8" width="11" height="11" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></svg>}
            </button>
          </div>
        )}
        {explanation && (
          <details className="br-message__explanation">
            <summary onKeyDown={toggleExplanationFromKeyboard}>Response Trace</summary>
            <div className="br-message__explanation-body">
              {explanation.summary && (
                <div>
                  <span className="br-message__explanation-label">Why</span>
                  <p>{explanation.summary}</p>
                </div>
              )}
              {explanation.factors.length > 0 && (
                <div>
                  <span className="br-message__explanation-label">Used</span>
                  <ul>{explanation.factors.map((factor) => <li key={factor}>{factor}</li>)}</ul>
                </div>
              )}
              {explanation.limitations && (
                <div>
                  <span className="br-message__explanation-label">Uncertainty</span>
                  <p>{explanation.limitations}</p>
                </div>
              )}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
